import React from 'react';
import {Input, TextArea} from 'shared-portal-components-v2';

import { classNames } from '@/utils/classNames.utils';

export interface StepSixInfoViewProps {
  nameValue: string;
  descriptionValue: string;
  remainingNameCharacters: number;
  remainingDescriptionCharacters: number;
  nameMaxLength: number;
  descriptionMaxLength: number;
  onNameChange: (nextValue: string) => void;
  onDescriptionChange: (nextValue: string) => void;
  nameHasError?: boolean;
  nameErrorMessage?: string;
  descriptionHasError?: boolean;
  descriptionErrorMessage?: string;
}

function StepSixInfoView(props: StepSixInfoViewProps): React.JSX.Element {
  const {
    nameValue,
    descriptionValue,
    remainingNameCharacters,
    remainingDescriptionCharacters,
    nameMaxLength,
    descriptionMaxLength,
    onNameChange,
    onDescriptionChange,
    nameHasError = false,
    nameErrorMessage,
    descriptionHasError = false,
    descriptionErrorMessage,
  } = props;

  const nameFieldId = React.useId();
  const descriptionFieldId = React.useId();

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">SIX Basics</h3>
        <p className="text-muted-foreground">
          Let&apos;s start with the essential information about your SIX.
        </p>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <label
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            htmlFor={nameFieldId}
          >
            SIX Name *
          </label>
          <p className={classNames('text-xs text-muted-foreground', nameHasError ? 'text-destructive' : '')}>
            {remainingNameCharacters} characters remaining
          </p>
        </div>

        <Input
          id={nameFieldId}
          name="name"
          value={nameValue}
          maxLength={nameMaxLength}
          placeholder="e.g., Q1 Marketing Campaign Files"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            onNameChange(event.currentTarget.value);
          }}
          aria-invalid={nameHasError}
          className={classNames(nameHasError ? 'border-destructive focus-visible:ring-destructive' : '')}
        />

        {nameHasError && nameErrorMessage !== undefined && (
          <p className="text-sm text-destructive">{nameErrorMessage}</p>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <label
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            htmlFor={descriptionFieldId}
          >
            Description (Optional)
          </label>
          <p className={classNames('text-xs text-muted-foreground', descriptionHasError ? 'text-destructive' : '')}>
            {remainingDescriptionCharacters} characters remaining
          </p>
        </div>

        <TextArea
          id={descriptionFieldId}
          name="description"
          value={descriptionValue}
          maxLength={descriptionMaxLength}
          placeholder="Optional details about this SIX..."
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            onDescriptionChange(event.currentTarget.value);
          }}
          aria-invalid={descriptionHasError}
          className={classNames(descriptionHasError ? 'border-destructive focus-visible:ring-destructive' : '')}
        />

        {descriptionHasError && descriptionErrorMessage !== undefined && (
          <p className="text-sm text-destructive">{descriptionErrorMessage}</p>
        )}
      </div>
    </div>
  );
}

export default StepSixInfoView;
